import {z} from 'zod';

export const salesLeaguePeriodSchema = z.enum(['week', 'month', 'quarter', 'year']);
export type SalesLeaguePeriod = z.infer<typeof salesLeaguePeriodSchema>;

export const salesLeaguePeriodRangeSchema = z.object({
  key: salesLeaguePeriodSchema,
  from: z.string(),
  to: z.string(),
  previousFrom: z.string(),
  previousTo: z.string(),
});
export type SalesLeaguePeriodRange = z.infer<typeof salesLeaguePeriodRangeSchema>;

export const salesLeagueBranchSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type SalesLeagueBranch = z.infer<typeof salesLeagueBranchSchema>;

export const salesLeagueTotalsSchema = z.object({
  vehiclesSold: z.number(),
  /** Same branch, previous window. Feeds the vehicles tile's period-over-period chip. */
  previousVehiclesSold: z.number(),
  /** `vehiclesSold - previousVehiclesSold`. This board's chip is the absolute delta. */
  vehiclesSoldChange: z.number(),
  /** Reps who sold at least one vehicle in the period, never the branch headcount. */
  salesReps: z.number(),
  /**
   * Decimal string at two places — `"8.07"` — while the tile renders one. Round for
   * display; the backend deliberately does not pre-round to the tile's precision.
   * Null when `salesReps` is 0: a ratio with no denominator, never 0.
   */
  vehiclesPerSalesRep: z.string().nullable(),
  /** Decimal string, not a number. Format from the string, never accumulate. */
  totalMargin: z.string().nullable(),
  /** Weighted by the backend. Never re-derive by averaging the margin column. */
  avgMarginPerVehicle: z.string().nullable(),
  /**
   * Highest per-vehicle margin on the board. Resolved by the backend so its tie-break —
   * strictly greater, leaving the title with the better-ranked rep — lives in one place;
   * never re-scan `rows` for the maximum. Null when margin is hidden or the board is empty.
   */
  bestMarginPerVehicle: z.string().nullable(),
  /** The rep holding it; match against `rows[].userId`. Null exactly when the amount is. */
  bestMarginUserId: z.string().nullable(),
});
export type SalesLeagueTotals = z.infer<typeof salesLeagueTotalsSchema>;

export const salesLeagueRowSchema = z.object({
  rank: z.number(),
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  isCurrentUser: z.boolean(),
  vehiclesSold: z.number(),
  marginPerVehicle: z.string().nullable(),
  /**
   * This rep's summed margin at this branch, in this period. Never `marginPerVehicle`
   * times `vehiclesSold` — the per-vehicle figure is rounded, and sales in a non-tenant
   * currency count toward `vehiclesSold` but add nothing to the margin.
   */
  totalMargin: z.string().nullable(),
  /** `previousRank - rank`; positive means moved up. Null when it held no previous rank. */
  trend: z.number().nullable(),
  /** Vehicles against the previous window. For a rep absent then, their full count. */
  vehiclesSoldChange: z.number(),
});
export type SalesLeagueRow = z.infer<typeof salesLeagueRowSchema>;

export const salesLeagueLeaderboardResponseSchema = z.object({
  period: salesLeaguePeriodRangeSchema,
  branch: salesLeagueBranchSchema,
  currency: z.string(),
  marginVisible: z.boolean(),
  totals: salesLeagueTotalsSchema,
  rows: z.array(salesLeagueRowSchema),
});
export type SalesLeagueLeaderboardResponse = z.infer<typeof salesLeagueLeaderboardResponseSchema>;

export const getSalesLeagueLeaderboardApiArgSchema = z.object({
  period: salesLeaguePeriodSchema,
  /**
   * Always sent explicitly. The backend would otherwise resolve the branch from the
   * `X-Branch` header, which is not part of the RTK cache key — so switching the active
   * branch in the top bar would leave a stale board on screen. Sending it makes the
   * branch part of the key.
   */
  branchId: z.string(),
});
export type GetSalesLeagueLeaderboardApiArg = z.infer<typeof getSalesLeagueLeaderboardApiArgSchema>;
export type GetSalesLeagueLeaderboardApiResponse = SalesLeagueLeaderboardResponse;

export const salesLeagueBranchBoardTotalsSchema = z.object({
  /** Branches on the board, including those that sold nothing. */
  branches: z.number(),
  vehiclesSold: z.number(),
  previousVehiclesSold: z.number(),
  /**
   * Absolute delta. This board renders a *percentage*, so it feeds
   * `getPeriodChangePercentage` from `vehiclesSold` and `previousVehiclesSold` instead —
   * this field is not the percentage. Only the rep board's chip reads it directly.
   */
  vehiclesSoldChange: z.number(),
  /**
   * Decimal string at two places while the tile renders one. Null when the board carries
   * no branch.
   */
  vehiclesPerBranch: z.string().nullable(),
  /**
   * The six margin fields are populated only when board-level `marginVisible` is true —
   * a visible total minus the visible rows would reveal a hidden branch's margin.
   * Decimal strings, never numbers.
   */
  totalMargin: z.string().nullable(),
  avgMarginPerVehicle: z.string().nullable(),
  previousTotalMargin: z.string().nullable(),
  previousAvgMarginPerVehicle: z.string().nullable(),
  /**
   * Highest per-vehicle margin on the board. Gated on board-level `marginVisible`, so the
   * best-margin card follows the board flag and not its rows — the one place the branch
   * board's two-level gate does not split. See
   * `libs/dms/modules/sales-league/docs/adr/0001-margin-visibility-is-gated-at-two-levels.md`.
   */
  bestMarginPerVehicle: z.string().nullable(),
  /** The branch holding it; match against `rows[].branchId`. Null exactly when the amount is. */
  bestMarginBranchId: z.string().nullable(),
});
export type SalesLeagueBranchBoardTotals = z.infer<typeof salesLeagueBranchBoardTotalsSchema>;

export const salesLeagueBranchRowSchema = z.object({
  rank: z.number(),
  branchId: z.string(),
  branchName: z.string(),
  /** Tenant-configured badge code; null when the tenant never set one. */
  branchCode: z.string().nullable(),
  /** True for the viewer's active branch — a highlight, never a promise. */
  isCurrentBranch: z.boolean(),
  /** The ACL's advisory answer to "may this viewer drill into this branch". */
  detailAvailable: z.boolean(),
  vehiclesSold: z.number(),
  /** Reps who sold at least one vehicle in the period, never the branch headcount. */
  sellers: z.number(),
  /**
   * `vehiclesSold / sellers` as a decimal string at two places, while the column renders
   * one. Null when `sellers` is 0 — a ratio with no denominator, never 0.
   */
  vehiclesPerSeller: z.string().nullable(),
  /**
   * `vehiclesSold - rows[0].vehiclesSold`: zero on the leading row, negative below it.
   * Measured against the top of the board, never against the row above, so the board and
   * its export draw the same gap. A tie for the lead leaves both rows at zero, so "leads"
   * is `rank === 1` and never `gapToLeader === 0`.
   */
  gapToLeader: z.number(),
  /** Null when margin is hidden for this branch, or the branch sold nothing. Never 0. */
  marginPerVehicle: z.string().nullable(),
  /**
   * This branch's summed margin. `"0.00"` for a branch that sold nothing, so null here
   * means only "hidden from you" — unlike `marginPerVehicle`, which conflates the two.
   * That makes it the honest per-row margin-visibility gate.
   */
  totalMargin: z.string().nullable(),
  /** `previousRank - rank`; positive means moved up. Null when it held no previous rank. */
  trend: z.number().nullable(),
  vehiclesSoldChange: z.number(),
});
export type SalesLeagueBranchRow = z.infer<typeof salesLeagueBranchRowSchema>;

export const salesLeagueBranchBoardResponseSchema = z.object({
  period: salesLeaguePeriodRangeSchema,
  currency: z.string(),
  marginVisible: z.boolean(),
  totals: salesLeagueBranchBoardTotalsSchema,
  /** Pre-sorted by rank ascending; ranks are sequential 1…N with no ties and no gaps. */
  rows: z.array(salesLeagueBranchRowSchema),
});
export type SalesLeagueBranchBoardResponse = z.infer<typeof salesLeagueBranchBoardResponseSchema>;

/** The wire params. `branchId` is absent by design — the board is always the whole tenant. */
export const getSalesLeagueBranchBoardApiArgSchema = z.object({
  period: salesLeaguePeriodSchema,
});

export const getSalesLeagueBranchBoardQueryArgSchema = getSalesLeagueBranchBoardApiArgSchema.extend(
  {
    activeBranchId: z.string(),
  }
);
export type GetSalesLeagueBranchBoardApiArg = z.infer<
  typeof getSalesLeagueBranchBoardQueryArgSchema
>;
export type GetSalesLeagueBranchBoardApiResponse = SalesLeagueBranchBoardResponse;

/**
 * A downloaded export, as an object URL rather than the `Blob` itself: RTK keeps a query's
 * result in the store, and a `Blob` there trips Redux's serializability check on every
 * action that touches the entry. The same trick as `getEmployeeAvatarUrl`.
 *
 * The URL owns the file until someone revokes it, which `downloadSalesLeagueCsv` does once
 * the download has been handed to the browser.
 *
 * The backend names the file — period, branch slug and the window's closing date — and
 * sends `Access-Control-Expose-Headers: Content-Disposition` so the name is readable here;
 * `fileName` is null only when that header does not arrive, and the caller then falls back
 * to a name of its own.
 */
export interface SalesLeagueCsvExport {
  fileUrl: string;
  fileName: string | null;
}

/**
 * The export endpoints take the read endpoints' parameters unchanged — the backend
 * dispatches the read query itself, so the two cannot drift into accepting different
 * periods or branches.
 */
export const exportSalesLeagueLeaderboardCsvApiArgSchema = getSalesLeagueLeaderboardApiArgSchema;
export type ExportSalesLeagueLeaderboardCsvApiArg = z.infer<
  typeof exportSalesLeagueLeaderboardCsvApiArgSchema
>;
export type ExportSalesLeagueLeaderboardCsvApiResponse = SalesLeagueCsvExport;

export const exportSalesLeagueBranchBoardCsvApiArgSchema = getSalesLeagueBranchBoardApiArgSchema;
export type ExportSalesLeagueBranchBoardCsvApiArg = z.infer<
  typeof exportSalesLeagueBranchBoardCsvApiArgSchema
>;
export type ExportSalesLeagueBranchBoardCsvApiResponse = SalesLeagueCsvExport;
